// Fence project math: posts, rails, pickets/boards, concrete, gate hardware.
// Supports picket, privacy (vertical board) and split-rail styles.
import { ceilTo, round } from '../lib/format'
import type { AccessoryLine } from '../data/companions'
import type { Locale } from '../i18n/locale'

export type FenceStyle = 'picket' | 'privacy' | 'split-rail'

export interface FenceInputs {
  totalLengthFt: number
  fenceStyle: FenceStyle
  postSpacingFt: number     // default 8
  railCount: number         // 2 or 3, default 2
  picketWidthIn: number     // inches, default 3.5
  gateSets: number          // each gate set = 1 gate + hardware
  wastePct: number          // 10-15, default 10
}

export interface FenceResult {
  posts: number
  rails: number             // total rail pieces (assumes 8 ft rails)
  pickets: number           // pickets or privacy boards (0 for split-rail)
  concreteBags60lb: number  // 2 per post
  gateSets: number
  lines: AccessoryLine[]
}

export const DEFAULT_FENCE: FenceInputs = {
  totalLengthFt: 100,
  fenceStyle: 'privacy',
  postSpacingFt: 8,
  railCount: 2,
  picketWidthIn: 3.5,
  gateSets: 1,
  wastePct: 10,
}

const CONCRETE_PER_POST = 2     // 60 lb bags per post
const RAIL_LENGTH_FT = 8        // standard rail length

export function computeFence(i: FenceInputs, locale: Locale = 'en'): FenceResult {
  const tr = (en: string, fr: string) => (locale === 'fr' ? fr : en)
  const lFt = Math.max(0, i.totalLengthFt)
  const spacingFt = Math.max(1, i.postSpacingFt)

  // Posts: one at each end of every section + one extra for the last end
  const posts = lFt > 0 ? ceilTo(lFt / spacingFt, 1) + 1 : 0
  const spans = Math.max(0, posts - 1)   // number of fence spans between posts

  // Rails: spans × railCount (each span gets railCount rails on each side — just 1 fence face)
  // Each span is postSpacingFt wide → need ceil(postSpacingFt / RAIL_LENGTH_FT) rail pieces per rail row
  const railsPerSpan = ceilTo(spacingFt / RAIL_LENGTH_FT, 1)
  const rawRails = spans * i.railCount * railsPerSpan
  const rails = rawRails > 0 ? ceilTo(rawRails * (1 + Math.max(0, i.wastePct) / 100), 1) : 0

  // Pickets / boards (not for split-rail)
  let pickets = 0
  if (i.fenceStyle !== 'split-rail') {
    const picketWidthIn = Math.max(1, i.picketWidthIn)
    // boards to cover the full fence length (gap between privacy boards is ~0, pickets have small gaps)
    const gapIn = i.fenceStyle === 'privacy' ? 0 : 0.5
    const effectiveWidthIn = picketWidthIn + gapIn
    const rawPickets = (lFt * 12) / effectiveWidthIn
    pickets = lFt > 0 ? ceilTo(rawPickets * (1 + Math.max(0, i.wastePct) / 100), 1) : 0
  }

  const concreteBags60lb = posts > 0 ? posts * CONCRETE_PER_POST : 0

  const lines: AccessoryLine[] = []

  lines.push({
    item: 'concrete mix 60 lb bag quikrete fence post',
    label: tr('Concrete bags (60 lb)', 'Sacs de béton (60 lb)'),
    kind: 'consumable',
    qty: concreteBags60lb,
    unit: 'bag',
    note: tr(`${CONCRETE_PER_POST} bags per post hole · set posts in concrete for stability`, `${CONCRETE_PER_POST} sacs par trou de poteau · scellez les poteaux dans le béton pour la stabilité`),
  })

  if (i.fenceStyle !== 'split-rail') {
    lines.push({
      item: 'fence rail 2x4 treated lumber 8 ft',
      label: tr('Fence rails (8 ft)', 'Lisses de clôture (8 pi)'),
      kind: 'consumable',
      qty: rails,
      unit: 'rail',
      note: tr(`8 ft rails · ${i.railCount} rail${i.railCount > 1 ? 's' : ''} per span · use pressure-treated lumber`, `Lisses de 8 pi · ${i.railCount} lisse${i.railCount > 1 ? 's' : ''} par travée · utilisez du bois traité sous pression`),
    })
    lines.push({
      item: i.fenceStyle === 'picket' ? 'picket fence board 6 ft' : 'cedar privacy fence board dog ear',
      label: i.fenceStyle === 'picket' ? tr('Fence pickets', 'Planches de clôture') : tr('Privacy boards', 'Planches d’intimité'),
      kind: 'consumable',
      qty: pickets,
      unit: 'board',
      note: tr(`${i.picketWidthIn} in wide · use pressure-treated or cedar for longevity`, `${i.picketWidthIn} po de largeur · utilisez du bois traité ou du cèdre pour la longévité`),
    })
  } else {
    lines.push({
      item: 'split rail fence rail cedar',
      label: tr('Split rails', 'Perches refendues'),
      kind: 'consumable',
      qty: rails,
      unit: 'rail',
      note: tr(`${i.railCount} rails per section · split-rail cedar has a natural rustic look`, `${i.railCount} perches par section · le cèdre refendu offre un look rustique naturel`),
    })
  }

  lines.push({
    item: 'fence post cap wood decorative',
    label: tr('Post caps', 'Capuchons de poteau'),
    kind: 'consumable',
    qty: posts,
    unit: 'cap',
    note: tr('Caps prevent water from sitting on end grain — extends post life significantly', 'Les capuchons empêchent l’eau de stagner sur le bout du grain — prolongent nettement la durée de vie du poteau'),
    optional: true,
  })

  if (i.gateSets > 0) {
    lines.push({
      item: 'gate hardware kit hinges latch',
      label: tr('Gate hardware kits', 'Trousses de quincaillerie de barrière'),
      kind: 'consumable',
      qty: i.gateSets,
      unit: 'kit',
      note: tr('Each kit includes 2 hinges + 1 latch — use heavy-duty hardware for larger gates', 'Chaque trousse comprend 2 pentures + 1 loquet — utilisez de la quincaillerie robuste pour les grandes barrières'),
    })
  }

  lines.push({
    item: 'fence wood stain sealer exterior',
    label: tr('Fence stain / sealer', 'Teinture / scellant pour clôture'),
    kind: 'consumable',
    qty: Math.max(1, ceilTo((lFt * 6) / 300, 1)),
    unit: 'gal',
    note: tr('~300 sq ft per gallon · both sides of a 6 ft fence · apply within first season', '~300 pi² par gallon · les deux côtés d’une clôture de 6 pi · appliquez dès la première saison'),
    optional: true,
  })

  lines.push({ item: 'post hole digger clamshell', label: tr('Post hole digger', 'Tarière à main'), kind: 'tool', note: tr('Rent a gas auger for more than 6 posts — saves hours.', 'Louez une tarière à essence pour plus de 6 poteaux — vous gagnerez des heures.') })
  lines.push({ item: 'fence screw nail nail gun framing', label: tr('Fence screws / nails', 'Vis / clous de clôture'), kind: 'consumable', qty: Math.max(1, ceilTo(pickets * 4 / 100, 1)), unit: 'lb', note: tr('~4 fasteners per board (2 per rail) · 1-5/8 in or 2 in deck screws', '~4 attaches par planche (2 par lisse) · vis de terrasse de 1-5/8 po ou 2 po'), optional: true })
  lines.push({ item: 'gravel pea gravel post hole drainage', label: tr('Gravel for post drainage', 'Gravier pour le drainage des poteaux'), kind: 'consumable', qty: posts, unit: 'bag', note: tr('4–6 in of gravel at bottom of post hole before setting post extends life', '4 à 6 po de gravier au fond du trou avant de poser le poteau prolonge sa durée de vie'), optional: true })

  return {
    posts,
    rails,
    pickets,
    concreteBags60lb,
    gateSets: Math.max(0, i.gateSets),
    lines,
  }
}
